import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import {
  TRANSITIONS_TO_ACCEPTED,
  TRANSITIONS_TO_CANCELED,
  TRANSITIONS_TO_DECLINED,
  TRANSITIONS_TO_DELIVERED,
  TRANSITIONS_TO_COMPLETED,
  TRANSITIONS_TO_REQUESTED,
  TRANSITIONS_TO_REVIEW,
  TRANSITIONS_TO_REVIEWED,
} from '../../transactions/transactionProcessBooking';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const INBOX_PAGE_SIZE = 10;

const lastTransitionsFilter = {
  upcoming: TRANSITIONS_TO_ACCEPTED,
  requests: TRANSITIONS_TO_REQUESTED,
  past: [
    ...TRANSITIONS_TO_DECLINED,
    ...TRANSITIONS_TO_CANCELED,
    ...TRANSITIONS_TO_COMPLETED,
    ...TRANSITIONS_TO_DELIVERED,
    ...TRANSITIONS_TO_REVIEW,
    ...TRANSITIONS_TO_REVIEWED,
  ],
};

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      const transactionsRefs = entityRefs(transactions);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs:
          payload.data.meta.page === 1
            ? transactionsRefs
            : [...state.transactionRefs, ...transactionsRefs],
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchOrdersOrSales = params => (dispatch, getState, sdk) => {
  const { tab, type, page = 1 } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: lastTransitionsFilter[type],
    include: [
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing',
      'listing.images',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'protectedData',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.landscape-crop',
      'variants.landscape-crop2x',
    ],
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

export const loadData = params => {
  return fetchOrdersOrSales(params);
};
